import {
  Component,
  DestroyRef,
  ElementRef,
  QueryList,
  ViewChildren,
  computed,
  inject,
  input,
  model,
  output,
  signal
} from '@angular/core';

const DEFAULT_EXPIRY_SECONDS = 120;
const DEFAULT_RESEND_SECONDS = 30;

@Component({
  selector: 'app-otp-input',
  imports: [],
  templateUrl: './otp-input.component.html',
  styleUrl: './otp-input.component.scss'
})
export class OtpInputComponent {
  readonly length = input(6);
  readonly code = model('');
  readonly resend = output<void>();

  private readonly destroyRef = inject(DestroyRef);

  @ViewChildren('otpBox') private readonly boxes!: QueryList<ElementRef<HTMLInputElement>>;

  protected readonly digits = signal<string[]>(new Array(this.length()).fill(''));
  protected readonly expirySeconds = signal(0);
  protected readonly resendSeconds = signal(0);

  protected readonly expiryLabel = computed(() => this.formatCountdown(this.expirySeconds()));
  protected readonly resendLabel = computed(() => this.formatCountdown(this.resendSeconds()));

  private countdownHandle?: ReturnType<typeof setInterval>;

  constructor() {
    this.destroyRef.onDestroy(() => this.clearTimers());
  }

  start(expirySeconds = DEFAULT_EXPIRY_SECONDS, resendSeconds = DEFAULT_RESEND_SECONDS): void {
    this.digits.set(new Array(this.length()).fill(''));
    this.code.set('');
    this.expirySeconds.set(expirySeconds);
    this.resendSeconds.set(resendSeconds);
    this.clearTimers();

    this.countdownHandle = setInterval(() => {
      this.expirySeconds.update((seconds) => Math.max(0, seconds - 1));
      this.resendSeconds.update((seconds) => Math.max(0, seconds - 1));

      if (this.expirySeconds() === 0) {
        this.clearTimers();
      }
    }, 1000);

    setTimeout(() => this.boxes?.first?.nativeElement.focus());
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/[^0-9]/g, '').slice(-1);

    const digits = [...this.digits()];
    digits[index] = value;
    this.digits.set(digits);
    this.code.set(digits.join(''));
    input.value = value;

    if (value && index < this.length() - 1) {
      this.boxes.get(index + 1)?.nativeElement.focus();
    }
  }

  onKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.digits()[index] && index > 0) {
      this.boxes.get(index - 1)?.nativeElement.focus();
    }
  }

  protected requestResend(): void {
    if (this.resendSeconds() > 0) return;
    this.resend.emit();
  }

  private clearTimers(): void {
    if (this.countdownHandle) {
      clearInterval(this.countdownHandle);
      this.countdownHandle = undefined;
    }
  }

  private formatCountdown(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
}
